import { cloneDeep } from 'lodash';
import request from '@/utils/requests';

const getters = {
  isEditMode: (state) => state.mode === 'EDIT',

  isCreateMode: (state) => state.mode === 'CREATE',

  getMode: (state) => state.mode,

  getEditedMaterial: (state) => state.editedMaterial,

  getEditedMaterialTitle: (state) => state.editedMaterial.title,

  getEditedMaterialDescription: (state) => state.editedMaterial.description,

  getMaterialGroupId: (state) => state.editedMaterial.material_group_id,

  getEditedMaterialFrequency: (state) => state.editedMaterial.frequency,

  getEditedMaterialAddressId: (state) => state.editedMaterial.address_id,

  getEditedMaterialQuantity: (state) => state.editedMaterial.quantity,

  getEditedMaterialBoxCount: (state) => state.editedMaterial.box_count,

  getEditedMaterialImages: (state) => state.editedMaterial.images,

  getEditedMaterialSdgGoals: (state) => state.updatedSdgGoals,

  getUpdatedImages: (state) => state.updatedImages || [],

  getMaterialToSave: async (state) => {
    const material = cloneDeep(state.editedMaterial);
    const updatedSdgGoals = cloneDeep(state.updatedSdgGoals);

    // Ensure updatedImages is always an array
    // Fallback to editedMaterial.images if updatedImages is null/empty
    // This handles the case where images weren't loaded into updatedImages (e.g., API didn't return them)
    let updatedImages = state.updatedImages;
    if (!updatedImages || (Array.isArray(updatedImages) && updatedImages.length === 0)) {
      // Try to use editedMaterial.images as fallback
      if (state.editedMaterial.images && Array.isArray(state.editedMaterial.images) && state.editedMaterial.images.length > 0) {
        updatedImages = state.editedMaterial.images.map(img => ({
          url: img.url || img.image_url,
          id: img.id,
        })).filter(img => img.url);
      } else {
        updatedImages = [];
      }
    }
    const allImages = Array.isArray(updatedImages) ? cloneDeep(updatedImages) : [];
    
    // Backend expects:
    // - Existing images: objects with `url` property (these are kept as-is)
    // - New images: objects with `body` property (base64, these will be uploaded)
    // Filter images based on whether they have `url` (existing) or `body` (new)
    const existingImages = allImages.filter((image) => image && image.url && !image.body);
    const newImages = allImages.filter((image) => image && image.body && !image.url);

    // Backend validation requires `name` property for all images
    // For existing images (with url), extract filename from URL or use placeholder
    // Only pick the required fields to avoid sending database fields (created_at, etc.)
    const existingImagesWithName = existingImages.map((image) => {
      let fileName = image.name;
      if (!fileName) {
        // Try to extract filename from URL
        fileName = 'existing-image.jpg'; // Default placeholder
        try {
          const urlParts = image.url.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          // Remove query parameters if any
          const fileNameWithoutQuery = lastPart.split('?')[0];
          if (fileNameWithoutQuery && fileNameWithoutQuery.length > 0) {
            fileName = fileNameWithoutQuery;
          }
        } catch (e) {
          // If URL parsing fails, use placeholder
          console.warn('Failed to extract filename from URL:', image.url);
        }
      }
      // Only return the fields the backend expects
      return {
        id: image.id,
        url: image.url,
        name: fileName,
      };
    });

    // Combine existing images (with url and name) and new images (with body and name)
    // Backend's updateMaterialImages function will handle them correctly:
    // - Images with url: kept if url is in the list
    // - Images with body: uploaded to S3 and new records created
    material.images = [...existingImagesWithName, ...newImages];

    material.sdg_goals = updatedSdgGoals;

    return material;
  },
};
const mutations = {
  SET_MODE: (state, mode) => {
    state.mode = mode;
  },

  SET_EDITED_MATERIAL: (state, editedMaterial) => {
    state.editedMaterial = editedMaterial;

    // Handle address_id - API includes address_id now, but also check Address/address object for backward compatibility
    // Sequelize may return 'address' (lowercase) or 'Address' (uppercase) depending on association name
    if (!state.editedMaterial.address_id) {
      if (editedMaterial.Address && editedMaterial.Address.id) {
        state.editedMaterial.address_id = Number(editedMaterial.Address.id);
      } else if (editedMaterial.address && editedMaterial.address.id) {
        state.editedMaterial.address_id = Number(editedMaterial.address.id);
      }
    } else {
      // Ensure address_id is a number
      state.editedMaterial.address_id = Number(state.editedMaterial.address_id);
    }

    // Handle material_group_id - API includes material_group_id now, but also check MaterialGroup/material_group object for backward compatibility
    // Sequelize may return 'material_group' (lowercase) or 'MaterialGroup' (uppercase) depending on association name
    // Always set it if MaterialGroup/material_group.id exists (even if material_group_id already exists in state)
    // This ensures consistency for historical data
    if (editedMaterial.MaterialGroup && editedMaterial.MaterialGroup.id) {
      state.editedMaterial.material_group_id = Number(editedMaterial.MaterialGroup.id);
    } else if (editedMaterial.material_group && editedMaterial.material_group.id) {
      state.editedMaterial.material_group_id = Number(editedMaterial.material_group.id);
    } else if (state.editedMaterial.material_group_id) {
      // Ensure material_group_id is a number
      state.editedMaterial.material_group_id = Number(state.editedMaterial.material_group_id);
    }
    // Always set material_group_code if MaterialGroup/material_group.code is available (even if material_group_id already exists)
    // This is important for historical data where material_group_id might be set but code is missing
    if (editedMaterial.MaterialGroup && editedMaterial.MaterialGroup.code) {
      state.editedMaterial.material_group_code = editedMaterial.MaterialGroup.code;
    } else if (editedMaterial.material_group && editedMaterial.material_group.code) {
      state.editedMaterial.material_group_code = editedMaterial.material_group.code;
    }

    // Handle condition_id - ensure it's preserved and is a number
    if (editedMaterial.condition_id !== undefined && editedMaterial.condition_id !== null) {
      state.editedMaterial.condition_id = Number(editedMaterial.condition_id);
    }

    // Handle SDG goals - support both array of objects and array of IDs
    if (editedMaterial?.sdg_goals && Array.isArray(editedMaterial.sdg_goals)) {
      if (editedMaterial.sdg_goals.length > 0) {
        // Check if it's array of objects with id property
        if (typeof editedMaterial.sdg_goals[0] === 'object' && editedMaterial.sdg_goals[0].id) {
          state.updatedSdgGoals = editedMaterial.sdg_goals.map((i) => i.id);
        } else if (typeof editedMaterial.sdg_goals[0] === 'number') {
          // Already array of IDs
          state.updatedSdgGoals = editedMaterial.sdg_goals;
        } else {
          state.updatedSdgGoals = [];
        }
      } else {
        state.updatedSdgGoals = [];
      }
    } else {
      state.updatedSdgGoals = [];
    }
  },

  SET_EDITED_MATERIAL_TITLE: (state, title) => {
    state.editedMaterial.title = title;
  },

  SET_EDITED_MATERIAL_DESCRIPTION: (state, description) => {
    state.editedMaterial.description = description;
  },

  SET_EDITED_MATERIAL_GROUP_ID: (state, materialGroupId) => {
    state.editedMaterial.material_group_id = materialGroupId;
  },

  SET_EDITED_MATERIAL_GROUP_CODE: (state, materialGroupCode) => {
    state.editedMaterial.material_group_code = materialGroupCode;
  },

  SET_EDITED_MATERIAL_FREQUENCY: (state, frequency) => {
    state.editedMaterial.frequency = frequency;
  },

  SET_EDITED_MATERIAL_ADDRESS_ID: (state, addressId) => {
    state.editedMaterial.address_id = addressId;
  },

  SET_EDITED_MATERIAL_CONDITION_ID: (state, conditionId) => {
    state.editedMaterial.condition_id = conditionId !== null && conditionId !== undefined ? Number(conditionId) : null;
  },

  SET_EDITED_MATERIAL_QUANTITY: (state, quantity) => {
    state.editedMaterial.quantity = parseInt(quantity, 10);
  },

  SET_EDITED_MATERIAL_BOX_COUNT: (state, boxCount) => {
    state.editedMaterial.box_count = boxCount !== null && boxCount !== undefined ? parseInt(boxCount, 10) : null;
  },

  SET_EDITED_MATERIAL_SDG_GOALS: (state, sdgGoals) => {
    state.updatedSdgGoals = sdgGoals;
  },

  SET_UPDATED_IMAGES: (state, imageData) => {
    state.updatedImages = imageData;
  },
};
const actions = {
  startEditMode: ({ commit }) => {
    commit('SET_MODE', 'EDIT');
  },

  startCreateMode: ({ commit }) => {
    commit('SET_MODE', 'CREATE');
  },

  setEditedMaterial: ({ commit }, editedMaterial) => {
    commit('SET_EDITED_MATERIAL', editedMaterial);
  },

  setEditedMaterialTitle: ({ commit }, title) => {
    commit('SET_EDITED_MATERIAL_TITLE', title);
  },

  setEditedMaterialDescription: ({ commit }, description) => {
    commit('SET_EDITED_MATERIAL_DESCRIPTION', description);
  },

  setEditedMaterialMaterialGroupId: ({ commit }, materialGroupId) => {
    commit('SET_EDITED_MATERIAL_GROUP_ID', materialGroupId);
  },

  setEditedMaterialMaterialGroupCode: ({ commit }, materialGroupCode) => {
    commit('SET_EDITED_MATERIAL_GROUP_CODE', materialGroupCode);
  },

  setEditedMaterialFrequency: ({ commit }, frequency) => {
    commit('SET_EDITED_MATERIAL_FREQUENCY', frequency);
  },

  setEditedMaterialAddressId: ({ commit }, addressId) => {
    commit('SET_EDITED_MATERIAL_ADDRESS_ID', addressId);
  },

  setEditedMaterialConditionId: ({ commit }, conditionId) => {
    commit('SET_EDITED_MATERIAL_CONDITION_ID', conditionId);
  },

  setEditedMaterialSdgGoals: ({ commit }, sdgGoals) => {
    commit('SET_EDITED_MATERIAL_SDG_GOALS', sdgGoals);
  },

  setEditedMaterialQuantity: ({ commit }, quantity) => {
    commit('SET_EDITED_MATERIAL_QUANTITY', quantity);
  },

  setEditedMaterialBoxCount: ({ commit }, boxCount) => {
    commit('SET_EDITED_MATERIAL_BOX_COUNT', boxCount);
  },

  setUpdatedImages: ({ commit }, imageData) => {
    commit('SET_UPDATED_IMAGES', imageData);
  },

  close: ({ commit }) => {
    commit('SET_MODE', null);
    commit('SET_UPDATED_IMAGES', null);
    commit('SET_EDITED_MATERIAL', {
      title: '',
      description: '',
      material_group_id: '',
      frequency: {},
      address_id: null,
      condition_id: null,
      images: [],
      sdg_goals: [],
      sub_listings: [],
      quantity: null,
      box_count: null,
    });
  },

  // eslint-disable-next-line no-shadow
  updateMaterial: async ({ getters, rootGetters, dispatch }, prebuiltMaterial) => {
    const material = await getters.getMaterialToSave;

    material.sub_listings = prebuiltMaterial.sub_listings;

    const companyId = rootGetters['auth/getUserCompanyId'];

    try {
      const response = await request(
        'PUT',
        `api/v2/materials/${material.id}`,
        rootGetters['auth/getToken'],
        material,
        { company_id: companyId, uuid: material.uuid }
      );
      dispatch('materials/setShouldRefetchMaterials', true, { root: true });
      // Don't close here - let the modal close first, then clean up
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  // eslint-disable-next-line no-shadow
  createMaterial: async ({ getters, rootGetters, dispatch }, prebuiltMaterial) => {
    const material = await getters.getMaterialToSave;

    material.sub_listings = prebuiltMaterial.sub_listings;
    const companyId = rootGetters['auth/getUserCompanyId'];

    try {
      const response = await request(
        'POST',
        'api/v2/materials',
        rootGetters['auth/getToken'],
        material,
        { company_id: companyId }
      );
      dispatch('materials/setShouldRefetchMaterials', true, { root: true });
      // Don't close here - let the modal close first, then clean up
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

const modules = {};
const state = {
  mode: null,
  editedMaterial: {
    title: '',
    description: '',
    material_group_id: '',
    frequency: {},
    address_id: null,
    images: [],
    sdg_goals: [],
    sub_listings: [],
    quantity: null,
    box_count: null,
  },
  updatedImages: [],
  updatedSdgGoals: [],
};

export default {
  namespaced: true,
  getters,
  mutations,
  actions,
  modules,
  state,
};
